import axios from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    // Chiamate fatte da pagine pubbliche: un 401 non deve innescare refresh/logout
    skipAuthRedirect?: boolean;
    // Endpoint pubblici che il backend vieta agli utenti loggati: la richiesta parte senza token né cookie
    skipAuth?: boolean;
  }
}

const baseURL = process.env.REACT_APP_BACKEND_URL;

console.log('baseURL:', baseURL);

const api = axios.create({
  baseURL: (baseURL || '') + '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - aggiunge il token Bearer a ogni richiesta
api.interceptors.request.use((config) => {
  const token = config.skipAuth ? null : localStorage.getItem('authToken');
  config.headers['Authorization'] = token ? `Bearer ${token}` : '';
  if (config.skipAuth) {
    // Con XHR i cookie same-origin (es. via proxy in dev) partono sempre: fetch con credentials 'omit' li esclude
    config.withCredentials = false;
    config.adapter = 'fetch';
  }
  return config;
});

// Stato per serializzare i refresh concorrenti
let isRefreshing = false;
let pendingRequests: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const forceLogout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('roles');
  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
    window.location.href = '/';
    return;
  }
  window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
};

// Response interceptor - gestisce il refresh automatico del token quando scade
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Per gli endpoint di autenticazione propaga l'errore al chiamante senza redirect
      if (originalRequest.url?.includes('/auth/signin') ||
          originalRequest.url?.includes('/auth/signup') ||
          originalRequest.url?.includes('/auth/password-reset') ||
          originalRequest.url?.includes('/auth/refresh-token')) {
        return Promise.reject(error);
      }

      // Chiamate delle pagine pubbliche: propaga l'errore al chiamante, niente refresh né logout
      if (originalRequest.skipAuthRedirect || originalRequest.skipAuth) {
        return Promise.reject(error);
      }

      // Se c'è già un refresh in corso, metti la richiesta in coda
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: (token: string) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        console.log('Token scaduto. Tentativo di refresh...');
        const response = await axios.post(
          `${baseURL || ''}/api/auth/refresh-token`,
          {},
          { withCredentials: true }
        );

        const newToken = response.data.token;
        localStorage.setItem('authToken', newToken);

        pendingRequests.forEach(({ resolve }) => resolve(newToken));
        pendingRequests = [];
        isRefreshing = false;

        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        console.log('Token refreshato con successo. Riprovando la richiesta originale...');
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Refresh token fallito. Reindirizzamento al login...', refreshError);
        pendingRequests.forEach(({ reject }) => reject(refreshError));
        pendingRequests = [];
        isRefreshing = false;
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
